import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react"
import { PathInfo, useAmbientFacilitiesContext } from "../AmbientFacilitiesContext"
import { parsePathQuery } from "./parsePathQuery"
import { parseTagQuery } from "./parseTagQuery"
import { Parsed, ParsingRequest } from "./types"


const useSyntaxMap = (databaseOptions: any[]) => useMemo(() => {
  return {
    'Help': (s:string) => s === '?' || s === '' || /^\s*\?\s*/.test(s),
    'Tag': (s:string) => s === '#' || /^\s*#\S+/.test(s),
    'At': (s:string) => s === '@' || /^\s*@\S+/.test(s),
    'Path': (s:string) => s === '/' || /^\s*\//i.test(s),
  }
}, [databaseOptions])


interface useQueryValueParserProps {
  pushParsedResult: (x: Parsed) => void,
}


export const useQueryValueParser = ({ pushParsedResult }: useQueryValueParserProps) => { 
  const { databaseOptions, schemaOptions, tableOptions, fieldOptions, tagOptions: tagOptions, setPathInfo, pathInfo } = useAmbientFacilitiesContext()
  const pathInfoRef = useRef(pathInfo)
  pathInfoRef.current = pathInfo

  const syntaxMap = useSyntaxMap(databaseOptions);
  
  const parsingRequests = useRef<ParsingRequest[]>([])
  
  const currParsingText = useRef('')
  const ctxData = {
    data: { 
      databaseOptions,
      schemaOptions,
      tableOptions,
      fieldOptions,
      tagOptions,
    }
  }
  const parsingReqCtxRef = useRef(ctxData)
  parsingReqCtxRef.current = ctxData

  const processRequestQueue = useCallback(() => {
    const { finished, notfinished } = 
      parsingRequests.current.filter(req => req.value === currParsingText.current)
        .map(req => req.process(parsingReqCtxRef.current.data))
        .reduce((acc, req) => {
          if (req.finished) 
               { acc.finished.push(req) } 
          else { acc.notfinished.push(req) }
          return acc
        }, { finished:[] as ParsingRequest[], notfinished:[] as ParsingRequest[] })
    
    for(var req of finished) {
      req.result && pushParsedResult(req.result)
    }
  
    parsingRequests.current = notfinished
  }, [])
  
  processRequestQueue()


  const enqueueParsingRequest = useCallback((s: string) => {
    if (parsingRequests.current.some(req => req.value === s)) { return }
    currParsingText.current = s

    const trimedS = (s || '').trim();

    const syntax = 
      (Object.keys(syntaxMap) as Array<keyof(typeof syntaxMap)>).reduce((acc, k) => {
        if (acc !== null) return acc
        return syntaxMap[k](trimedS) ? k : null
      }, null as keyof(typeof syntaxMap) | null) 
      || 'FieldFuzzy'

    switch (syntax) {
      case 'Help': {
        pushParsedResult({
          raw: s,
          ast: { kind: 'Help', value: '?' },
          guess: [],
        })
        break;
      }
      case 'Tag': {
        parseTagQuery(s, parsingRequests)
        break;
      }
      case 'At': {
        pushParsedResult({
          raw: s,
          ast: { kind: 'At', prefix: '@', value: trimedS.replace(/^@/, '') },
          guess: [],
        })
        break;
      }
      case 'Path': {
        parsePathQuery(s, pathInfoRef, setPathInfo, pushParsedResult, parsingRequests)
        break;
      }
      case 'FieldFuzzy': {
        const fields = trimedS.split(',').map(x => x.trim())
        const iEmpty = fields.indexOf('')
        const error = iEmpty > -1 ? 'empty string is not a valid field name' : undefined;
        let position: [number,number]|undefined = undefined

        if (error) {
          const pos0 = iEmpty === 0 
            ? 0 
            : s.indexOf(',', 
              [...(Array(iEmpty - 1).keys())].reduce((acc, i) => {
                const n = s.indexOf(fields[i], acc)
                return n + fields[i].length
              }, -1))
          const pos1 = pos0 === 0
            ? s.indexOf(',')
            : s.indexOf(',', pos0+1) > -1 ? (s.indexOf(',', pos0+1) - 1) : s.length - 1
          position = [pos0, pos1]
        }
        
        pushParsedResult({
          raw: s,
          ast: { kind: 'FieldFuzzy', value: trimedS, fields },
          guess: fields.length > 1 ? [] : [
            { kind: 'Tag', prefix: '#', value: '#'+trimedS },
            { kind: 'At', prefix: '@', value: '@'+trimedS },
          ],
          error,
          position,
        })
        break;
      }
    }

    processRequestQueue(); // immediately process requests using current data within the same tick

  }, [syntaxMap, pushParsedResult, processRequestQueue])

  return { enqueueParsingRequest }
}

